import { createContext, useContext, useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import jwt from 'jwt-decode'
import axios from '../../services/api'
import { AuthCtxTypes, Props, TokenTypes } from './auth.interfaces'
import { DecodedTokenTypes } from '../../interfaces/decodedToken'
import { useQueryClient } from '@tanstack/react-query'

const authProviderCtx = createContext<AuthCtxTypes>({
  token: null,
  userType: null,
  uniqueCode: null,
  authClient: undefined,
  logout: () => null,
  storeToken: () => null,
  setUniqueCode: () => null,
})

export const useAuthContext = (): AuthCtxTypes => useContext(authProviderCtx)

const AuthProvider = ({ children }: Props) => {
  // GET TOKEN - localStorage when "keep me signed in" was checked, otherwise
  // sessionStorage so the session ends with the browser tab.
  const getToken = () => {
    const stored =
      localStorage.getItem('token') ?? sessionStorage.getItem('token')
    if (stored === null) return null
    try {
      const accessToken: TokenTypes = JSON.parse(stored)
      return accessToken?.access ?? null
    } catch {
      // Not JSON (stale format, or another app sharing this origin). Drop it
      // instead of throwing during render, which would blank the whole app.
      localStorage.removeItem('token')
      sessionStorage.removeItem('token')
      return null
    }
  }

  // ROLE. It is a claim on the access token, so routing never has to wait for
  // (or depend on) the profile request: /auth/user/{id}/ sits behind
  // HasActiveSubscription and 403s for an organization whose subscription is
  // still pending, which must not lock the user out of Plan & Billing.
  const getUserType = (accessToken: AuthCtxTypes['token']) => {
    if (accessToken === null) return null
    try {
      return jwt<DecodedTokenTypes>(accessToken).user_type ?? null
    } catch {
      return null
    }
  }

  const [token, setToken] = useState<AuthCtxTypes['token']>(getToken())
  const [uniqueCode, setUniqueCode] = useState<string | null>(null)
  const { cache } = useSWRConfig()
  const queryClient = useQueryClient()

  // SAVE TOKEN. `keepSignedIn` decides whether it outlives the browser session.
  const storeToken = (token: TokenTypes, keepSignedIn: boolean = true) => {
    // Clear first: the token is written into sessionStorage when the user opts
    // out of staying signed in, so clearing afterwards would wipe it.
    sessionStorage.clear()

    if (keepSignedIn) {
      localStorage.setItem('token', JSON.stringify(token))
    } else {
      localStorage.removeItem('token')
      sessionStorage.setItem('token', JSON.stringify(token))
    }

    setToken(token.access)
  }

  // LOGOUT USER
  const logout = () => {
    cache.delete('authClient')
    queryClient.clear()
    localStorage.removeItem('token')
    sessionStorage.removeItem('token')
    setToken(null)
  }

  // GET THE LOGGED IN USER
  const getAuthClient = async () => {
    const { user_id }: DecodedTokenTypes = jwt(token!)
    const response = await axios.get(`/auth/user/${user_id}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    return response.data.data
  }

  const { data: authClient } = useSWR(
    token ? 'authClient' : null,
    getAuthClient,
  )

  const ctx: AuthCtxTypes = {
    token,
    userType: getUserType(token),
    uniqueCode,
    authClient,
    logout,
    storeToken,
    setUniqueCode,
  }

  return (
    <authProviderCtx.Provider value={ctx}>{children}</authProviderCtx.Provider>
  )
}

export default AuthProvider
